import { usePrevious } from '@e3dc-react/shell/Hooks'
import { useTheme } from '@mui/material'
import { useMediaQuery } from '@mui/system'
import { useState, useCallback, useEffect } from 'react'
import { CategoricalChartState } from 'recharts/types/chart/types'
import { DataWithTime } from 'src/Libs/chart-kit'
import { useSystemChartsContext } from '../providers/SystemChartsProvider'

export type ZoomIndexType = string | number | null

type UseZoomChartsHook<T extends DataWithTime> = {
  zoomedData: T[]
  handleMouseDown: (e: CategoricalChartState) => void
  handleMouseMove: (e: CategoricalChartState) => void
  handleMouseUp: () => void
  resetZoom: () => void
  zoomStartIndex: ZoomIndexType
  zoomEndIndex: ZoomIndexType
  zoomWasReset: boolean
  isZoomedIn: boolean
  zoomedFrom: string | number | null
  zoomedTo: string | number | null
}

type Props<T extends DataWithTime> = {
  data: T[]
}

const MINIMUM_HORIZONTAL_SCROLL_VALUE = 65 // To avoid accidental zooming create a minimum zoom range adjust as needed
const CHART_Y_OFFSET = 2 // To avoid accidental zooming on vertical scroll, adjust as needed

/**
 * This hook manages the zoom functionality for charts.
 * It allows users to zoom in on a specific range of data points by clicking and dragging.
 * It also provides a way to reset the zoom to the original data.
 * Takes in data with time, which can be either `ts` or `time` as a property and returns the zoomed data along with handlers for mouse events.
 * Additionally returns zoomedFrom and zoomedTo representing the timestamp boundaries of the current zoom range.
 */
export default function useZoomCharts<T extends DataWithTime>({ data: originalData }: Props<T>): UseZoomChartsHook<T> {
  const theme = useTheme()
  const downMd = useMediaQuery(theme.breakpoints.down('md'))
  const [zoomedData, setZoomedData] = useState<T[] | undefined>()
  const [zoomStartIndex, setZoomStartIndex] = useState<ZoomIndexType>(null)
  const [zoomEndIndex, setZoomEndIndex] = useState<ZoomIndexType>(null)
  const [zoomWasReset, setZoomWasReset] = useState(false)
  const [chartX, setChartX] = useState<number | undefined>()
  const [chartY, setChartY] = useState<number | undefined>()
  const previousOriginalData = usePrevious(JSON.stringify(originalData))
  const { resetChartSelection } = useSystemChartsContext()
  const isZoomedIn = zoomedData !== undefined

  const handleMouseDown = useCallback((e: CategoricalChartState): void => {
    if (e === undefined) return
    setZoomStartIndex(e.activeLabel || null)
    setChartX(e.chartX)
    setChartY(e.chartY)
  }, [])

  const handleMouseMove = useCallback(
    (e: CategoricalChartState): void => {
      if (e === undefined) return
      if (zoomStartIndex !== null) {
        const value = e.activeLabel || null
        const isVerticalScroll = Math.abs((e.chartY || 0) - (chartY || 0)) + CHART_Y_OFFSET > Math.abs((e.chartX || 0) - (chartX || 0))
        const minimumHorizontalScrollValueNotReached = Math.abs((e.chartX || 0) - (chartX || 0)) <= MINIMUM_HORIZONTAL_SCROLL_VALUE
        const ignoreMouseMove = downMd && zoomEndIndex === null && (minimumHorizontalScrollValueNotReached || isVerticalScroll)

        if (ignoreMouseMove) return
        setZoomEndIndex(value)
      }
    },
    [zoomStartIndex, chartY, chartX, downMd, zoomEndIndex]
  )

  const handleMouseUp = useCallback((): void => {
    if (zoomStartIndex !== null && zoomEndIndex !== null && zoomStartIndex !== zoomEndIndex) {
      const startIndex = originalData?.findIndex(d => {
        if ('ts' in d) return d.ts === zoomStartIndex
        if ('time' in d) return d.time === zoomStartIndex
        return null
      })
      const endIndex = originalData?.findIndex(d => {
        if ('ts' in d) return d.ts === zoomEndIndex
        if ('time' in d) return d.time === zoomEndIndex
        return null
      })
      if (startIndex !== -1 && endIndex !== -1) {
        const minIndex = Math.min(startIndex, endIndex)
        const maxIndex = Math.max(startIndex, endIndex)
        setZoomedData(originalData?.slice(minIndex, maxIndex + 1))
      }
      resetChartSelection()
    }
    setZoomStartIndex(null)
    setZoomEndIndex(null)
    setZoomWasReset(false)
  }, [zoomStartIndex, resetChartSelection, zoomEndIndex, originalData])

  const resetZoom = useCallback((): void => {
    if (!zoomedData) return
    setZoomWasReset(true)
    setZoomedData(undefined)
    setZoomStartIndex(null)
  }, [zoomedData])

  useEffect(() => {
    /* reset zoom if data changes */
    if (previousOriginalData !== JSON.stringify(originalData)) resetZoom()
  }, [previousOriginalData, originalData, resetZoom])

  const zoomedFrom = zoomedData ? getTimestampFromDataItem(zoomedData?.[0]) : null
  const zoomedTo = zoomedData ? getTimestampFromDataItem(zoomedData?.[zoomedData.length - 1]) : null

  return {
    zoomedData: zoomedData || originalData,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    resetZoom,
    zoomStartIndex,
    zoomEndIndex,
    zoomWasReset,
    isZoomedIn,
    zoomedFrom,
    zoomedTo,
  }
}

/**
 * Helper function to extract timestamp from data item
 */
const getTimestampFromDataItem = (item: DataWithTime): string | number | null => {
  if ('ts' in item && item.ts !== undefined) return item.ts
  if ('time' in item && item.time !== undefined) return item.time
  return null
}
